import React from 'react';
import VerifyEmailBanner from '../VerifyEmailBanner';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../i18n';
import FeedbackWidget from '../FeedbackWidget';
import { Button } from '../ui/button';
import { LogOut, Menu, LayoutDashboard, Building2, Briefcase, Settings, Download, Share, Users, Store, Package, Search, FileQuestion, Receipt, BarChart3, FileStack, Info, ChevronDown, Video } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import LanguageSelector from '../common/LanguageSelector';
import { usePwaInstallPrompt, isIOS, isPwaInstalled, detectBrowser } from '../../hooks/usePwaInstallPrompt';
import { Alert, AlertDescription } from '../ui/alert';
import { AISupportButton } from '../AISupportChat';
import AISupportChat from '../AISupportChat';
import { NotificationBell } from '../Notifications';
import api, { getLogoUrl } from '../../lib/api';
import { isPageVisible } from '../../lib/planGating';
import EmailVerificationBanner from '../EmailVerificationBanner';



// Logo with custom image
const Logo = ({ size = 'default', customLogoUrl }) => (
  <div className={`flex items-center gap-2 ${size === 'small' ? '' : ''}`}>
    <img
      src={customLogoUrl || "/assets/pocketfiler-header-logo.png"}
      alt="PocketFiler"
      className={size === 'small' ? 'h-20' : 'h-24'}
      style={{ objectFit: 'contain' }}
    />
  </div>
);

function Sidebar({ mobile = false, onClose }) {
  const location = useLocation();
  const { user, activeOrganization, logout, refreshUser } = useAuth();
  const userPlan = activeOrganization?.plan || "free";
  const { t } = useTranslation();


  // Navigation items — ordered by core business workflow
  const navItems = [
    { label: t('nav.dashboard'), route: '/dashboard' },
    { label: t('nav.deals'), route: '/deals' },
    { label: t('nav.contacts') || t('nav.clients'), route: '/clients' },
    { label: t('nav.documents'), route: '/documents' },
    { label: 'My Documents', route: '/my-documents' },
    { label: t('nav.invoices') || 'Invoices', route: '/invoices' },
    { label: t('nav.templates') || 'Templates', route: '/templates' },
    { label: t('nav.contractTemplates') || 'Contracts', route: '/contract-templates' },
    { label: t('nav.tasks') || 'Tasks', route: '/tasks' },
    { label: t('nav.messages'), route: '/messages' },
    { label: t('nav.meetings') || 'Meetings', route: '/meetings' },
    { label: t('nav.virtualOffices'), route: '/offices' },
    { label: t('nav.analytics') || 'Analytics', route: '/analytics' },
    { label: t('nav.companyProfile') || 'Company Profile', route: '/company-profile' },
    { label: t('nav.products') || 'Products', route: '/products' },
    { label: t('nav.directory') || 'Directory', route: '/directory' },
    { label: t('nav.rfq') || 'RFQ', route: '/rfq' },
    { label: 'Compliance', route: '/compliance' },
    { label: 'Intelligence', route: '/intelligence' },
    { label: t('nav.events') || 'Events', route: '/events' },
  ];


  // API Keys: available to org owners on Growth+ plans
  const apiPlans = ["growth", "scale", "enterprise"];
  if (!user?.is_user && apiPlans.includes(userPlan)) {
    navItems.push({ label: t('nav.developerSettings') || 'API Keys & Webhooks', route: '/developer-settings' });
  }

  return (
    <div className="flex flex-col h-full">
      <EmailVerificationBanner />
      {/* Logo */}
      <div className="p-4 border-b">
        <Logo
          customLogoUrl={(() => {
            const base = getLogoUrl(activeOrganization?.logo_url, activeOrganization?._id);
            return base && activeOrganization?.logo_url
              ? `${base}?v=${encodeURIComponent(activeOrganization.logo_url)}`
              : base;
          })()}
        />
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto min-h-0">
        {navItems.filter((item) => isPageVisible(item.route, userPlan)).map((item) => {
          const isActive = location.pathname === item.route ||
            (item.route === '/offices' && location.pathname.startsWith('/virtual-offices'));
          return (
            <Link
              key={item.route}
              to={item.route}
              onClick={onClose}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`flex items-center gap-3 px-3 py-2.5 min-h-[48px] rounded-lg transition-colors ${isActive
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
            >
              <span className="font-medium">{item.label}</span>
            </Link>
          );
        })}
      </nav>

      {/* User section */}
      <div className="p-4 border-t">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <span className="font-semibold text-primary">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate">{user?.name}</p>
            <p className="text-sm text-muted-foreground truncate">{user?.email}</p>
          </div>
        </div>
        <div className="space-y-1">
          {/* Only organization owners can access settings */}
          {!user?.is_user && (
            <Link
              to="/settings"
              onClick={onClose}
              data-testid="nav-settings"
              className="flex items-center gap-3 px-3 py-2 min-h-[48px] rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors w-full"
            >
              <span className="font-medium">{t('nav.settings')}</span>
            </Link>
          )}
          <button
            onClick={() => {
              logout();
              onClose?.();
            }}
            data-testid="nav-logout"
            className="flex items-center gap-3 px-3 py-2 min-h-[48px] rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors w-full"
          >
            <LogOut className="h-5 w-5" aria-hidden="true" />
            <span className="font-medium">{t('nav.logout')}</span>
          </button>
        </div>
      </div>
      <FeedbackWidget />
      </div>
  );
}

// Bottom Navigation for Mobile
function MobileBottomNav() {
  const location = useLocation();

  const { user } = useAuth();

  const { t } = useTranslation();
  const navItems = [
    { label: t('nav.dashboard'), icon: LayoutDashboard, route: '/dashboard' },
    { label: t('nav.contacts') || t('nav.clients'), icon: Users, route: '/clients' },
    { label: t('nav.virtualOffices'), icon: Building2, route: '/offices' },
    { label: t('nav.deals'), icon: Briefcase, route: '/deals' },
  ];

  if (!user?.is_user) {
    navItems.push({ label: t('nav.settings'), icon: Settings, route: '/settings' });
  }

  return (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-background border-t z-50 safe-area-inset-bottom">
      <div className="flex justify-around items-center">
        {navItems.map((item) => {
          const isActive = location.pathname === item.route ||
            (item.route === '/offices' && location.pathname.startsWith('/virtual-offices')) ||
            (item.route === '/deals' && location.pathname.startsWith('/deal'));
          const Icon = item.icon;
          return (
            <Link
              key={item.route}
              to={item.route}
              className={`flex flex-col items-center justify-center py-2 px-3 min-h-[56px] min-w-[64px] transition-colors ${isActive
                ? 'text-primary'
                : 'text-muted-foreground'
                }`}
              aria-label={item.label}
            >
              <Icon className="h-5 w-5 mb-1" aria-hidden="true" />
              <span className="text-[10px] font-medium">{item.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

/**
 * Cross-browser PWA install button shown in the top header.
 *
 * Behaviour per browser:
 *  Chrome / Edge  – `beforeinstallprompt` fires → `canInstall` becomes true →
 *                   button appears and triggers the native prompt on click.
 *                   The browser also shows its own address-bar install icon.
 *
 *  Opera desktop  – Opera does NOT support PWA installation on desktop at all
 *                   (only Chrome and Edge do on desktop per web.dev spec).
 *                   We show an informational popover telling the user to switch
 *                   to Chrome or Edge to install the app.
 *
 *  Firefox        – Does NOT fire `beforeinstallprompt`. Firefox has its own
 *                   separate address-bar install icon. We show a tooltip
 *                   pointing users to look at their address bar.
 *
 *  Safari / other – Not supported; nothing is rendered.
 */
function PwaInstallButton({ canInstall, isInstalled, onInstall, t }) {
  const [showInstructions, setShowInstructions] = React.useState(false);
  const browser = React.useMemo(() => detectBrowser(), []);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  // Never show on mobile — MobileInstallBanner handles that case
  if (isMobile) return null;
  // Never show if already installed as a PWA
  if (isInstalled || isPwaInstalled()) return null;

  // --- Chrome / Edge ---
  // Only render when the native event has fired (browser also shows address-bar icon)
  if (browser === 'chrome' || browser === 'edge') {
    if (!canInstall) return null;
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={onInstall}
        className="hidden sm:flex min-h-[40px] gap-2"
        aria-label="Install PocketFiler app"
        data-testid="install-app-button"
      >
        <Download className="h-4 w-4" />
        <span className="hidden md:inline">{t('pwa.installApp')}</span>
      </Button>
    );
  }

  // --- Opera desktop ---
  // Opera desktop does not support PWA installation (confirmed: only Chrome & Edge
  // support desktop PWA install per the web.dev specification).
  // Show a persistent info button that opens a popover directing users to Chrome/Edge.
  if (browser === 'opera') {
    return (
      <div className="relative hidden sm:block">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowInstructions(prev => !prev)}
          className="min-h-[40px] gap-2 text-muted-foreground"
          aria-label="PWA install info for Opera"
          data-testid="install-app-button-opera"
        >
          <Download className="h-4 w-4" />
          <span className="hidden md:inline">{t('pwa.installApp')}</span>
          <Info className="h-3 w-3 opacity-60" />
        </Button>

        {showInstructions && (
          <div className="absolute right-0 top-full mt-2 w-80 bg-popover border rounded-lg shadow-lg p-4 z-50 text-sm animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="flex items-start gap-2 mb-2">
              <Info className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <p className="font-semibold text-foreground">
                {t('pwa.opera.title')}
              </p>
            </div>
            <p className="text-muted-foreground mb-3 leading-relaxed">
              {t('pwa.opera.desktopNotSupported')}
            </p>
            <div className="bg-muted/60 rounded-md p-3 space-y-1.5">
              <p className="text-xs font-semibold text-foreground uppercase tracking-wide">
                {t('pwa.opera.recommendedBrowsers')}
              </p>
              <div className="flex items-center gap-2 text-muted-foreground">
                <span className="text-base">🌐</span>
                <span><strong className="text-foreground">Google Chrome</strong> — {t('pwa.opera.chromeHint')}</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <span className="text-base">🌐</span>
                <span><strong className="text-foreground">Microsoft Edge</strong> — {t('pwa.opera.edgeHint')}</span>
              </div>
            </div>
            <button
              onClick={() => setShowInstructions(false)}
              className="mt-3 text-xs text-muted-foreground hover:text-foreground underline"
            >
              {t('common.dismiss')}
            </button>
          </div>
        )}
      </div>
    );
  }

  // --- Firefox ---
  // Firefox does not fire `beforeinstallprompt`. It has its own address-bar install
  // icon when PWA criteria are met. Show a hover tooltip directing users there.
  if (browser === 'firefox') {
    return (
      <div className="relative hidden sm:block group">
        <Button
          variant="ghost"
          size="sm"
          className="min-h-[40px] gap-2 text-muted-foreground"
          aria-label="How to install PocketFiler in Firefox"
          data-testid="install-app-button-firefox"
        >
          <Download className="h-4 w-4" />
          <span className="hidden md:inline">{t('pwa.installApp')}</span>
          <Info className="h-3 w-3 opacity-60" />
        </Button>

        <div className="absolute right-0 top-full mt-2 w-64 bg-popover border rounded-lg shadow-lg p-3 z-50 text-sm
                        invisible group-hover:visible opacity-0 group-hover:opacity-100
                        transition-opacity duration-200 pointer-events-none">
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
            <p className="text-muted-foreground">
              {t('pwa.firefox.hint')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default function AppLayout() {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [showIosHint, setShowIosHint] = React.useState(false);
  const { canInstall, promptInstall, isInstalled } = usePwaInstallPrompt();
  const { activeOrganization, user } = useAuth();
  const { t } = useTranslation();

  // Check if we should show iOS install hint
  React.useEffect(() => {
    if (isIOS() && !isPwaInstalled()) {
      // Show hint after a short delay, only once per session
      const hasShownHint = sessionStorage.getItem('pwa-ios-hint-shown');
      if (!hasShownHint) {
        const timer = setTimeout(() => {
          setShowIosHint(true);
          sessionStorage.setItem('pwa-ios-hint-shown', 'true');
        }, 5000);
        return () => clearTimeout(timer);
      }
    }
  }, []);

  const handleInstallClick = async () => {
    const result = await promptInstall();
    console.log('Install prompt result:', result.outcome);
  };

  // Convert hex color to Tailwind's HSL format (h s% l%)
  const hexToHSL = (hex) => {
    let r = 0, g = 0, b = 0;
    if (hex.length === 4) {
      r = parseInt(hex[1] + hex[1], 16);
      g = parseInt(hex[2] + hex[2], 16);
      b = parseInt(hex[3] + hex[3], 16);
    } else if (hex.length === 7) {
      r = parseInt(hex.slice(1, 3), 16);
      g = parseInt(hex.slice(3, 5), 16);
      b = parseInt(hex.slice(5, 7), 16);
    }
    r /= 255; g /= 255; b /= 255;
    let cmin = Math.min(r, g, b),
      cmax = Math.max(r, g, b),
      delta = cmax - cmin,
      h = 0, s = 0, l = 0;

    if (delta === 0) h = 0;
    else if (cmax === r) h = ((g - b) / delta) % 6;
    else if (cmax === g) h = (b - r) / delta + 2;
    else h = (r - g) / delta + 4;

    h = Math.round(h * 60);
    if (h < 0) h += 360;
    l = (cmax + cmin) / 2;
    s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
    s = +(s * 100).toFixed(1);
    l = +(l * 100).toFixed(1);

    return `${h} ${s}% ${l}%`;
  };

  // Inject brand colors onto the document root so ALL elements (including
  // Radix UI portals rendered outside app-shell) pick them up.
  React.useEffect(() => {
    const root = document.documentElement;
    if (activeOrganization?.primary_color) {
      root.style.setProperty('--primary', hexToHSL(activeOrganization.primary_color));
      root.style.setProperty('--ring', hexToHSL(activeOrganization.primary_color));
    } else {
      root.style.removeProperty('--primary');
      root.style.removeProperty('--ring');
    }
    if (activeOrganization?.accent_color) {
      root.style.setProperty('--accent', hexToHSL(activeOrganization.accent_color));
    } else {
      root.style.removeProperty('--accent');
    }
  }, [activeOrganization?.primary_color, activeOrganization?.accent_color]);

  return (
    <div className="app-shell">
      {/* iOS Install Hint Banner — fixed so it doesn't participate in the app-shell flex row */}
      {showIosHint && (
        <div className="fixed bottom-20 left-0 right-0 z-[55] px-3 md:hidden animate-in slide-in-from-bottom duration-300">
          <Alert className="bg-primary/5 shadow-lg border">
            <Share className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between gap-2">
              <span className="text-sm">
                {t('pwa.iosHint') || 'Tap the Share icon and "Add to Home Screen" to install PocketFiler.'}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowIosHint(false)}
                className="flex-shrink-0 h-8"
              >
                {t('common.dismiss')}
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside data-tour="sidebar" className="sidebar hidden lg:flex flex-col">
        <Sidebar />
      </aside>

      {/* Main content */}
      <div className="main-content">
        {/* Header */}
        <header className="header px-3 sm:px-4 py-3 flex items-center gap-3 sm:gap-4">
          {/* Mobile menu - Hidden on very small screens since we have bottom nav */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button
                variant="ghost"
                size="icon"
                className="min-h-[48px] min-w-[48px]"
                data-testid="mobile-menu-button"
                aria-label="Open navigation menu"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72">
              <Sidebar mobile onClose={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>

          {/* Logo for mobile */}
          <div className="lg:hidden">
            <Logo
              size="small"
              customLogoUrl={(() => {
                const base = getLogoUrl(activeOrganization?.logo_url, activeOrganization?._id);
                return base && activeOrganization?.logo_url
                  ? `${base}?v=${encodeURIComponent(activeOrganization.logo_url)}`
                  : base;
              })()}
            />
          </div>

          <div className="flex-1" />

          {/* Install App Button — cross-browser aware */}
          <PwaInstallButton
            canInstall={canInstall}
            isInstalled={isInstalled}
            onInstall={handleInstallClick}
            t={t}
          />

          {/* AI Support Button (Desktop) */}
          <div className="hidden sm:block">
            <AISupportButton />
          </div>

          {/* Notification Bell */}
          <NotificationBell />

          {/* Language Selector */}
          <LanguageSelector variant="compact" />
        </header>

        <VerifyEmailBanner user={user} />
        {/* Page content - Extra padding at bottom for mobile nav and breathing room above browser bottom */}
        <main className="flex-1 p-3 sm:p-4 lg:p-6 overflow-auto pb-20 md:pb-4 lg:pb-12">
          <Outlet />
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />

      {/* AI Support FAB (Mobile) */}
      <AISupportChat showFAB={true} />
      <FeedbackWidget />
      </div>
  );
}
